import styles from './FJSlideCompare.module.less';
import FJSlideCompareIcon from '../../../assets/svg/slideCompare.svg?react';
import PropTypes from 'prop-types';
import FJLocalStore from '../../../store/FJLocalStore';
import classNames from 'classnames';
import { useCallback, useRef, useMemo, useState, useEffect } from 'react';

const FJSlideCompare = props => {
    const {
        active, // 是否滑动对比
        editImageRect, // 区域图片的rect
        compareLineScale, // 对比线比例
        originImageSize, // 原图尺寸
        editImageSize, // 编辑图片尺寸
        changeCompareLineScale, // 改变对比线比例
        zoom, // 缩放比例

        className,
    } = props;

    // 用于确保界面更新后，重新计算正确样式
    const [isUpdate, setIsUpdate] = useState(false);

    useEffect(() => {
        setIsUpdate(!isUpdate);
    }, [active]);

    const mouseDownPoint = useRef({ x: 0, y: 0 });
    const beforeBoxRef = useRef(null);
    const afterBoxRef = useRef(null);
    const compareLineRef = useRef(null);

    const mouseMoveHandler = useCallback(
        e => {
            const movePoint = { x: e.clientX, y: e.clientY };
            const moveX = movePoint.x - mouseDownPoint.current.x;

            const offsetX = moveX / editImageRect.width;

            let newCompareLineScale = offsetX + compareLineScale;

            if (newCompareLineScale > 1) {
                newCompareLineScale = 1;
            }

            if (newCompareLineScale < 0) {
                newCompareLineScale = 0;
            }

            changeCompareLineScale(newCompareLineScale);
        },
        [changeCompareLineScale, compareLineScale, editImageRect],
    );

    const mouseUpHandler = useCallback(
        e => {
            e.stopPropagation();

            window.removeEventListener('mousemove', mouseMoveHandler);
        },
        [mouseMoveHandler],
    );

    const mouseDownHandler = useCallback(
        e => {
            e.stopPropagation();
            mouseDownPoint.current = { x: e.clientX, y: e.clientY };

            window.addEventListener('mousemove', mouseMoveHandler);
            window.addEventListener('mouseup', mouseUpHandler);

            return () => {
                window.removeEventListener('mousemove', mouseMoveHandler);
                window.removeEventListener('mouseup', mouseUpHandler);
            };
        },
        [mouseMoveHandler, mouseUpHandler],
    );

    const beforeBoxStyleByZoom = useMemo(() => {
        const beforeBox = beforeBoxRef.current;
        const compareLine = compareLineRef.current;
        if (!beforeBox || !compareLine) {
            return {};
        }

        const clientWidth = beforeBox.clientWidth;
        const clientHeight = beforeBox.clientHeight;

        if (clientWidth === 0 || clientHeight === 0) {
            return {
                opacity: 0,
            };
        }

        const compareLineLeft = editImageRect.width * compareLineScale;
        const newWidth = clientWidth * zoom;
        const newHeight = clientHeight * zoom;

        const left = 12 * zoom;

        const showRect = {
            top: 0,
            right: Math.max(left + newWidth - compareLineLeft, 0) / zoom,
            bottom: 0,
            left: 0,
        };

        return {
            left: 12 * zoom,
            bottom: 12 * zoom,
            clipPath: `inset(${showRect.top}px ${showRect.right}px ${showRect.bottom}px ${showRect.left}px)`,
            transform: `translateX(${(newWidth - clientWidth) / 2}px) translateY(${-(newHeight - clientHeight) / 2}px) scale(${zoom}) `,
        };
    }, [zoom, editImageRect, compareLineScale, isUpdate]);

    const afterBoxStyleByZoom = useMemo(() => {
        const afterBox = afterBoxRef.current;
        const compareLine = compareLineRef.current;
        if (!afterBox || !compareLine) {
            return {};
        }

        const clientWidth = afterBox.clientWidth;
        const clientHeight = afterBox.clientHeight;

        if (clientWidth === 0 || clientHeight === 0) {
            return {
                opacity: 0,
            };
        }

        const compareLineLeft = editImageRect.width * compareLineScale;

        const newWidth = clientWidth * zoom;
        const newHeight = clientHeight * zoom;

        const right = editImageRect.width - 12 * zoom - newWidth;

        const showRect = {
            top: 0,
            right: 0,
            bottom: 0,
            left: Math.max(compareLineLeft + 2 - right, 0) / zoom,
        };

        return {
            right: 12 * zoom,
            bottom: 12 * zoom,
            clipPath: `inset(${showRect.top}px ${showRect.right}px ${showRect.bottom}px ${showRect.left}px)`,
            transform: `translateX(${-(newWidth - clientWidth) / 2}px) translateY(${-(newHeight - clientHeight) / 2}px) scale(${zoom}) `,
        };
    }, [zoom, editImageRect, compareLineScale, isUpdate]);

    return (
        <div
            className={classNames(styles.compare_box, className, !active && styles.hide)}
            style={{
                width: editImageRect.width,
                height: editImageRect.height,
                left: editImageRect.x,
                top: editImageRect.y,
            }}
        >
            <div
                className={classNames(styles.resize_box, styles.before_box)}
                ref={beforeBoxRef}
                style={beforeBoxStyleByZoom}
            >
                <span className={styles.title_text}>{FJLocalStore._('before')}</span> {originImageSize.width}*
                {originImageSize.height}
            </div>

            <div
                className={classNames(styles.resize_box, styles.after_box)}
                ref={afterBoxRef}
                style={afterBoxStyleByZoom}
            >
                <span className={styles.title_text}>{FJLocalStore._('after')}</span> {editImageSize.width}*
                {editImageSize.height}
            </div>

            <div
                className={styles.compare_line}
                style={{ left: `${compareLineScale * 100}%` }}
                onMouseDown={mouseDownHandler}
                ref={compareLineRef}
            >
                <FJSlideCompareIcon className={styles.compare_icon} />
            </div>
        </div>
    );
};

FJSlideCompare.propTypes = {
    editImageRect: PropTypes.object.isRequired,
    compareLineScale: PropTypes.number.isRequired,
    originImageSize: PropTypes.object.isRequired,
    editImageSize: PropTypes.object.isRequired,
    active: PropTypes.bool.isRequired,
    changeCompareLineScale: PropTypes.func.isRequired,
    zoom: PropTypes.number.isRequired,
    className: PropTypes.string,
};

export default FJSlideCompare;
